import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Boxes, ChevronDown, ChevronUp, Download, RotateCcw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import commonStyles from '../../styles/common.module.css';
import styles from './BasketsPage.module.css';

const BasketsPage = ({ embedded = false, allProjects = false } = {}) => {
  const [data, setData] = useState({ summary: {}, results: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [conflict, setConflict] = useState('all');
  const [stage, setStage] = useState('');
  const [fullness, setFullness] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [contents, setContents] = useState({});
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => ({
    project_id: allProjects ? undefined : (localStorage.getItem('selected_project_id') || undefined),
    search: search.trim() || undefined,
    conflict: conflict !== 'all' ? conflict : undefined,
    stage: stage || undefined,
    fullness: fullness !== 'all' ? fullness : undefined,
  }), [allProjects, search, conflict, stage, fullness]);

  const loadBaskets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/repairs/baskets/occupied/', { params });
      setData(response.data || { summary: {}, results: [] });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось загрузить занятые корзины');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = window.setTimeout(loadBaskets, 250);
    return () => window.clearTimeout(timer);
  }, [loadBaskets]);

  const toggleBasket = async (basketId) => {
    if (expandedId === basketId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(basketId);
    if (contents[basketId]) return;
    try {
      const response = await api.get(`/repairs/baskets/${basketId}/contents/`);
      setContents(previous => ({ ...previous, [basketId]: response.data.equipment || [] }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось открыть корзину');
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const response = await api.get('/repairs/baskets/export-occupied/', {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `занятые_корзины_${new Date().toISOString().slice(0, 10)}.xlsx`;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      window.setTimeout(() => {
        anchor.remove();
        URL.revokeObjectURL(url);
      }, 60_000);
    } catch (error) {
      toast.error('Не удалось сформировать выгрузку корзин');
    } finally {
      setExporting(false);
    }
  };

  const summary = data.summary || {};
  const rows = data.results || [];

  return (
    <div className={`${embedded ? styles.embeddedPage : commonStyles.pageContainer} ${commonStyles.fade}`}>
      <section className={styles.header}>
        <div>
          <div className={styles.title}><Boxes size={30} /><h1>{allProjects ? 'Корзины всех проектов' : 'Занятые корзины'}</h1></div>
          <p>{allProjects ? 'Все занятые корзины по доступным проектам и сервисным центрам' : 'Фактическое содержимое корзин и контроль смешения этапов'}</p>
        </div>
        <button className={styles.exportButton} onClick={exportXlsx} disabled={exporting || !rows.length}>
          <Download size={18} /> {exporting ? 'Формирование…' : 'Выгрузить XLSX'}
        </button>
      </section>

      <section className={styles.summaryGrid}>
        <article><span>Найдено корзин</span><strong>{summary.occupied_baskets || 0}</strong></article>
        <article><span>Аппаратов в выборке</span><strong>{summary.equipment || 0}</strong></article>
        <article className={`${summary.stage_conflicts ? styles.warningCard : ''} ${styles.clickableCard}`} onClick={() => setConflict('only')} title="Показать только корзины со смешанными этапами">
          <span>Смешано этапов</span><strong>{summary.stage_conflicts || 0}</strong>
        </article>
        <article className={styles.clickableCard} onClick={() => setFullness('full')} title="Показать полностью заполненные корзины"><span>Заполнено полностью</span><strong>{summary.full_baskets || 0}</strong></article>
      </section>

      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Search size={18} />
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Номер или тип корзины…" />
          </div>
          <div className={styles.filters}>
            <select value={conflict} onChange={event => setConflict(event.target.value)} aria-label="Смешение этапов">
              <option value="all">Все корзины</option>
              <option value="only">Только смешанные</option>
              <option value="without">Без смешения</option>
            </select>
            <select value={stage} onChange={event => setStage(event.target.value)} aria-label="Этап оборудования">
              <option value="">Все этапы</option>
              <option value="awaiting_diagnosis">Ожидает диагностики</option>
              <option value="in_diagnosis">На диагностике</option>
              <option value="awaiting_repair">Ожидает ремонта</option>
              <option value="in_repair">В ремонте</option>
              <option value="awaiting_parts">Ожидание ЗИП</option>
              <option value="awaiting_qc">Ожидает контроля качества</option>
              <option value="in_qc">На контроле качества</option>
              <option value="awaiting_cleaning">Ожидает клининга</option>
              <option value="in_cleaning">На клининге</option>
              <option value="in_packaging">На упаковке</option>
              <option value="packaged">Упакован</option>
              <option value="awaiting_shipping">Ожидает отправки</option>
            </select>
            <select value={fullness} onChange={event => setFullness(event.target.value)} aria-label="Заполнение корзины">
              <option value="all">Любое заполнение</option>
              <option value="full">Заполненные полностью</option>
              <option value="available">Есть свободные места</option>
            </select>
            {(search || conflict !== 'all' || stage || fullness !== 'all') && (
              <button className={styles.resetButton} type="button" onClick={() => { setSearch(''); setConflict('all'); setStage(''); setFullness('all'); }}>
                <RotateCcw size={15} /> Сбросить
              </button>
            )}
          </div>
          <span className={styles.resultCount}>{rows.length} корзин</span>
        </div>

        {loading ? (
          <div className={styles.empty}>Загрузка…</div>
        ) : !rows.length ? (
          <div className={styles.empty}>{allProjects ? 'Занятых корзин в доступных проектах нет' : 'Занятых корзин в выбранном проекте нет'}</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Корзина</th><th>Заказчик / город</th><th>Проект</th><th>Этап</th><th>Заполнение</th><th aria-label="Открыть" /></tr></thead>
              <tbody>
                {rows.map(row => (
                  <React.Fragment key={row.id}>
                    <tr className={row.has_stage_conflict ? styles.conflictRow : ''} onClick={() => toggleBasket(row.id)}>
                      <td><strong>{row.name}</strong><small>{row.requests.join(', ')}</small></td>
                      <td>{row.customer}<small>{row.city}</small></td>
                      <td className={styles.projectCell} title={row.projects.join(', ')}>{row.projects.join(', ') || '—'}</td>
                      <td>
                        <div className={styles.stageList} title={row.stage_labels.join(' / ')}>
                          {row.has_stage_conflict && <AlertTriangle className={styles.conflictIcon} size={16} />}
                          {row.stage_labels.map(label => <span key={label} className={row.has_stage_conflict ? styles.conflictBadge : styles.stageBadge}>{label}</span>)}
                        </div>
                      </td>
                      <td><strong>{row.current_count}</strong> / {row.capacity}</td>
                      <td>{expandedId === row.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</td>
                    </tr>
                    {expandedId === row.id && (
                      <tr className={styles.contentsRow}><td colSpan="6">
                        {!contents[row.id] ? <div className={styles.innerLoading}>Загрузка содержимого…</div> : (
                          <div className={styles.contentsTableWrap}>
                            <table className={styles.contentsTable}>
                              <thead><tr><th>Заявка</th><th>Серийный номер</th><th>Модель</th><th>Этап</th><th>Статус ремонта</th><th>Ответственный</th></tr></thead>
                              <tbody>{contents[row.id].map(item => (
                                <tr key={item.id}>
                                  <td>{item.request_number}</td><td>{item.serial_number}</td><td>{item.equipment_model}</td>
                                  <td>{item.location_status_display}</td><td>{item.repair_status_display}</td><td>{item.engineer || '—'}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default BasketsPage;
